import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { findItem, canSee, rememberTab, slug } from '../../lib/nav';
import { cn } from '../../lib/utils';

/**
 * Wraps a screen that is one tab of a consolidated page. Renders the routed tab
 * strip (each tab is its own URL) above the active screen, remembers the last
 * tab used, and only shows tabs the user is allowed to open.
 */
export function TabbedPage({ itemId, children }) {
  const location = useLocation();
  const { user } = useAuth();
  const perms = useEffectivePermissions();
  const found = findItem(itemId);
  const ctx = { role: user?.role, perms };

  const tabs = (found?.item?.tabs || []).filter((t) => canSee(t, ctx));
  const active = tabs.find((t) => location.pathname === t.href || location.pathname.startsWith(t.href + '/'));

  useEffect(() => {
    if (active) rememberTab(itemId, active.href);
  }, [itemId, active]);

  if (!found) return children;

  return (
    <div className="space-y-5" data-testid={`tabbed-page-${itemId}`}>
      {tabs.length > 1 && (
        <div className="border-b border-line overflow-x-auto scrollbar-thin" role="tablist" data-testid={`tabstrip-${itemId}`}>
          <div className="flex gap-1 min-w-max">
            {tabs.map((t) => (
              <NavLink
                key={t.href}
                to={t.href}
                role="tab"
                data-testid={`tabnav-${slug(t.name)}`}
                className={({ isActive }) => cn(
                  'relative inline-flex items-center px-4 min-h-[44px] text-sm font-medium transition-colors whitespace-nowrap',
                  'border-b-2 -mb-px',
                  isActive
                    ? 'border-indigo-500 text-fg'
                    : 'border-transparent text-fg-muted hover:text-fg hover:border-line'
                )}
              >
                {t.name}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <div>{children}</div>
    </div>
  );
}

export default TabbedPage;
